import {
    Client as SwitcherClient,
    ClientOptions
} from "@switcherstudio/api-core-client";
import store from "store/store";
import { setLoading } from "store/loading/slice";
import { addNotification } from "store/notification/slice";
import { NotificationType } from "store/notification/types";

const API_BASE_URL =
    import.meta.env.VITE_API_URL ||
    "https://silversunnapi-develop.azurewebsites.net";

export class ClientCore extends SwitcherClient {
    _http: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> };

    constructor(
        options: ClientOptions = new ClientOptions(),
        http: {
            fetch(url: RequestInfo, init?: RequestInit): Promise<Response>;
        } = {
            fetch: (
                url: RequestInfo,
                init?: RequestInit | undefined
            ): Promise<Response> => {
                const state = store.getState();
                if (init && init.headers && state.user.ticket) {
                    (init.headers as { [key: string]: string })[
                        "Authorization"
                    ] = "bearer " + state.user.ticket!.access_token;
                }

                const request = fetch(url, init);

                request.catch(() => {
                    store.dispatch(
                        addNotification({
                            type: NotificationType.Danger,
                            message: "login:server-error",
                            messageOptions: { allowDuplicates: false }
                        })
                    );

                    return request;
                });

                if (options.showLoading) {
                    store.dispatch(setLoading(1));
                    request.finally(() => store.dispatch(setLoading(-1)));
                }

                return request;
            }
        }
    ) {
        super(options, API_BASE_URL, http);
        this._http = http;
    }

    //Removes a destination from a scheduled livestream (sc-56803).
    //Hand-written against `DELETE /api/Broadcasts/{id}/StreamSettings/{id}` because the
    //generated client predates the endpoint. NSwag derives method names from the
    //operationId, so regenerating the client produces this same name and supersedes
    //this override - delete it then, the way `stripeIntents_GetSetupIntent` in
    //`client.ts` would be if it didn't also need the Turnstile header.
    broadcasts_RemoveBroadcastDestination(
        broadcastId: string,
        streamSettingId: string
    ): Promise<void> {
        if (broadcastId === undefined || broadcastId === null)
            throw new Error(
                "The parameter 'broadcastId' must be defined and cannot be null."
            );
        if (streamSettingId === undefined || streamSettingId === null)
            throw new Error(
                "The parameter 'streamSettingId' must be defined and cannot be null."
            );

        const url_ = `${API_BASE_URL}/api/Broadcasts/${encodeURIComponent(
            broadcastId
        )}/StreamSettings/${encodeURIComponent(streamSettingId)}`;

        const options_ = {
            method: "DELETE",
            headers: {
                Accept: "application/json"
            }
        } as RequestInit;

        return this._http.fetch(url_, options_).then((_response: Response) => {
            return this.transformResult(url_, _response, () =>
                Promise.resolve()
            );
        });
    }
}

// Just export this so we don't have to construct new ones all over the place
// unless custom options are required.
export const client = new ClientCore();
